import { useEffect, useRef } from 'react';

export type UseAutoVerticalScrollOptions = {
  /** When false, no automatic scrolling. */
  enabled?: boolean;
  /** Vertical speed in CSS pixels per second. */
  pixelsPerSecond?: number;
  /** Pause at the bottom (ms) before jumping back to the top. */
  pauseBottomMs?: number;
};

/**
 * Slowly scrolls a vertical overflow container for kiosk/TV displays.
 * Disabled when `prefers-reduced-motion: reduce`. Pauses while pointer is over the element.
 */
export function useAutoVerticalScroll(
  scrollRef: React.RefObject<HTMLElement | null>,
  {
    enabled = true,
    pixelsPerSecond = 26,
    pauseBottomMs = 2200,
  }: UseAutoVerticalScrollOptions = {}
): void {
  const pausedByPointer = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;

    let raf = 0;
    let last = performance.now();
    let phase: 'down' | 'pause-bottom' = 'down';
    let pauseUntil = 0;
    let attachedEl: HTMLElement | null = null;

    const cancelRaf = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const onEnter = () => {
      pausedByPointer.current = true;
    };
    const onLeave = () => {
      pausedByPointer.current = false;
    };

    const tick = (now: number) => {
      const node = scrollRef.current;
      if (!node) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const dt = Math.min(0.12, (now - last) / 1000);
      last = now;

      if (pausedByPointer.current) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
      if (maxScroll <= 2) {
        node.scrollTop = 0;
        raf = requestAnimationFrame(tick);
        return;
      }

      if (node.scrollTop > maxScroll + 1) {
        node.scrollTop = maxScroll;
      }

      if (phase === 'pause-bottom') {
        if (now < pauseUntil) {
          raf = requestAnimationFrame(tick);
          return;
        }
        node.scrollTop = 0;
        phase = 'down';
        raf = requestAnimationFrame(tick);
        return;
      }

      const next = node.scrollTop + pixelsPerSecond * dt;
      if (next >= maxScroll - 0.75) {
        node.scrollTop = maxScroll;
        phase = 'pause-bottom';
        pauseUntil = now + pauseBottomMs;
      } else {
        node.scrollTop = next;
      }
      raf = requestAnimationFrame(tick);
    };

    const startRaf = () => {
      cancelRaf();
      raf = requestAnimationFrame((now) => {
        last = now;
        raf = requestAnimationFrame(tick);
      });
    };

    const attachTo = (el: HTMLElement) => {
      if (attachedEl === el) return;
      if (attachedEl) {
        attachedEl.removeEventListener('pointerenter', onEnter);
        attachedEl.removeEventListener('pointerleave', onLeave);
      }
      attachedEl = el;
      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointerleave', onLeave);
      phase = 'down';
      startRaf();
    };

    const sync = () => {
      const el = scrollRef.current;
      if (!el) return;
      attachTo(el);
    };

    sync();
    const t0 = window.setTimeout(sync, 0);
    const t1 = window.setTimeout(sync, 120);
    const t2 = window.setTimeout(sync, 400);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      cancelRaf();
      if (attachedEl) {
        attachedEl.removeEventListener('pointerenter', onEnter);
        attachedEl.removeEventListener('pointerleave', onLeave);
        attachedEl = null;
      }
    };
  }, [enabled, pauseBottomMs, pixelsPerSecond, scrollRef]);
}
